import { useMemo } from "react";

import { useIntl } from "react-intl";

import {
    GameLensPreviewValidationError,
    getGameLensPreviewValidationMessage,
    validateGameLensPreview,
} from "../../lens-preview/validation";
import { type LensPreviewMediaItem } from "../../shared/state";
import { messages } from "../components/messages.generated";

interface UseWebLensPreviewValidationParams {
    previewMediaItems: LensPreviewMediaItem[];
    /** The preview editor's last rejection, which preserves the specific duration verdict. */
    previewValidationMessage: string | null;
}

/** Why a Web Lens cannot be submitted yet, or null when its required video is valid. */
export const useWebLensPreviewValidation = ({
    previewMediaItems,
    previewValidationMessage,
}: UseWebLensPreviewValidationParams): { errorMessage: string | null; hasVideoPreview: boolean } => {
    const intl = useIntl();
    const hasVideoPreview = previewMediaItems.some((item) => item.isVideo);

    const errorMessage = useMemo(() => {
        const validationError = validateGameLensPreview({ previewMediaItems, previewValidationMessage });

        if (validationError === GameLensPreviewValidationError.MissingVideo) {
            return intl.formatMessage(messages.webLensPreviewRequirement);
        }

        const durationMessage = getGameLensPreviewValidationMessage(validationError);
        return durationMessage ? intl.formatMessage(durationMessage) : null;
    }, [intl, previewMediaItems, previewValidationMessage]);

    return { errorMessage, hasVideoPreview };
};
